/**
 * API Response Utilities
 *
 * Common utilities for handling the standardized API response structure:
 * {
 *   success: boolean,
 *   data: { data: T[], page: number, pageSize: number, totalElements: number, totalPages: number },
 *   error: string | null,
 *   timestamp: number
 * }
 */

/**
 * Paginated API Response (from backend)
 */
export interface PaginatedApiResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Standard API Wrapper Response
 */
export interface ApiWrapperResponse<T> {
  success: boolean;
  data: T;
  error?: string | null;
  timestamp?: number;
}

/**
 * Complete Paginated Response (nested structure)
 */
export interface NestedPaginatedResponse<T> {
  success: boolean;
  data: {
    data: T[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  error?: string | null;
  timestamp?: number;
}

/**
 * Extracted Pagination Metadata
 */
export interface PaginationMetadata {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Unwrapped Paginated Data
 */
export interface UnwrappedPaginatedData<T> {
  data: T[];
  pagination: PaginationMetadata;
}

/**
 * Extract data array from nested paginated response
 *
 * @param response - API response with nested pagination structure
 * @returns Array of data items
 *
 * @example
 * const response = await api.get('/cases');
 * const cases = extractPaginatedData(response);
 */
export function extractPaginatedData<T>(
  response: NestedPaginatedResponse<T> | null | undefined,
): T[] {
  if (!response?.success || !response?.data) {
    return [];
  }
  return response.data.data || [];
}

/**
 * Extract pagination metadata from nested paginated response
 *
 * @param response - API response with nested pagination structure
 * @returns Pagination metadata
 *
 * @example
 * const response = await api.get('/cases');
 * const pagination = extractPaginationMetadata(response);
 * console.log(`Page ${pagination.page} of ${pagination.totalPages}`);
 */
export function extractPaginationMetadata<T>(
  response: NestedPaginatedResponse<T> | null | undefined,
): PaginationMetadata {
  const defaultMetadata: PaginationMetadata = {
    page: 1,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  if (!response?.success || !response?.data) {
    return defaultMetadata;
  }

  const { page, pageSize, totalElements, totalPages } = response.data;

  return {
    page: page || 1,
    pageSize: pageSize || 20,
    totalElements: totalElements || 0,
    totalPages: totalPages || 0,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

/**
 * Unwrap nested paginated response into data and metadata
 *
 * @param response - API response with nested pagination structure
 * @returns Object with data array and pagination metadata
 *
 * @example
 * const response = await api.get('/cases');
 * const { data, pagination } = unwrapPaginatedResponse(response);
 * console.log(`Found ${data.length} items, page ${pagination.page}`);
 */
export function unwrapPaginatedResponse<T>(
  response: NestedPaginatedResponse<T> | null | undefined,
): UnwrappedPaginatedData<T> {
  return {
    data: extractPaginatedData(response),
    pagination: extractPaginationMetadata(response),
  };
}

/**
 * Extract simple data from wrapped API response
 *
 * @param response - API response with success/data/error structure
 * @returns Data or null if not successful
 *
 * @example
 * const response = await api.get('/case/123');
 * const caseData = extractApiData(response);
 */
export function extractApiData<T>(
  response: ApiWrapperResponse<T> | null | undefined,
): T | null {
  if (!response?.success) {
    return null;
  }
  return response.data;
}

/**
 * Check if API response was successful
 *
 * @param response - API response
 * @returns True if response was successful
 */
export function isApiSuccess<T>(
  response:
    | ApiWrapperResponse<T>
    | NestedPaginatedResponse<T>
    | null
    | undefined,
): boolean {
  return response?.success === true;
}

/**
 * Get error message from API response
 *
 * @param response - API response
 * @returns Error message or null
 */
export function getApiError<T>(
  response:
    | ApiWrapperResponse<T>
    | NestedPaginatedResponse<T>
    | null
    | undefined,
): string | null {
  if (!response || response.success) {
    return null;
  }
  return response.error || "An unknown error occurred";
}

/**
 * Transform backend paginated response to frontend format
 * Useful for React Query or state management
 *
 * @param response - Backend API response
 * @param transform - Optional transform function for each item
 * @returns Unwrapped data with optional transformation
 *
 * @example
 * const response = await api.get('/cases');
 * const { data, pagination } = transformPaginatedResponse(response, transformCase);
 */
export function transformPaginatedResponse<TBackend, TFrontend = TBackend>(
  response: NestedPaginatedResponse<TBackend> | null | undefined,
  transform?: (item: TBackend) => TFrontend,
): UnwrappedPaginatedData<TFrontend> {
  const unwrapped = unwrapPaginatedResponse(response);

  if (!transform) {
    return unwrapped as unknown as UnwrappedPaginatedData<TFrontend>;
  }

  return {
    data: unwrapped.data.map(transform),
    pagination: unwrapped.pagination,
  };
}
